import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import dayjs from 'dayjs'
import { Box, Stack } from '@mui/material'
import { Text } from 'ui-kit'
import { FormSubmitWithRecaptcha } from '../Forms/FormSubmitWithRecaptcha'
import { SALESFORCE_IFRAME_PROPS } from '../Forms/submitToSalesforce'
import { useSubmitSalesforceLeadMutation } from '../Forms/useSubmitSalesforceLeadMutation'
import { buildLeadFormSchema } from './buildLeadFormSchema'
import { buildLeadPayload } from './buildLeadPayload'
import { LeadFormField } from './LeadFormField'
import type { LeadFormConfig, LeadFormSurface, LeadFormValues } from './types'

type LeadFormProps = {
  form: LeadFormConfig
  surface?: LeadFormSurface
  /** Name of the product page the form is on, for `currentProduct` fields. */
  productName?: string
  /** Runs a few seconds after a successful submit. */
  onSubmitted?: () => void
}

const SUCCESS_REDIRECT_MS = 5000

const submitButtonSx = {
  light: {
    width: '12.5rem',
    bgcolor: '#071a2a',
    color: '#fff',
    border: '3px solid #071a2a',
    '&:hover': { bgcolor: '#071a2a' },
  },
  dark: {
    mt: '2rem',
    bgcolor: 'white.main',
    color: 'black.main',
    '&:hover': { bgcolor: 'white.main' },
  },
}

const defaultValuesFor = (form: LeadFormConfig): LeadFormValues =>
  Object.fromEntries(
    form.fields
      .filter(field => field.type !== 'hidden')
      .map(field => [
        field.salesforceName,
        field.type === 'checkbox' ? false : field.defaultValue,
      ]),
  )

/** A Salesforce Web-to-Lead form drawn from a CMS Lead Form entry. */
export function LeadForm({
  form,
  surface = 'light',
  productName,
  onSubmitted,
}: LeadFormProps) {
  const idPrefix = useId()
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [submitLead, submitState] = useSubmitSalesforceLeadMutation()
  const schema = useMemo(() => buildLeadFormSchema(form.fields), [form])
  const redirectTimeoutId = useRef<number | undefined>(undefined)
  const textColor = surface === 'dark' ? 'white' : 'black'

  const {
    watch,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<LeadFormValues>({
    resolver: zodResolver(schema),
    defaultValues: defaultValuesFor(form),
    mode: 'onBlur',
  })

  const values = watch()

  useEffect(() => () => window.clearTimeout(redirectTimeoutId.current), [])

  const onSubmit = handleSubmit(async submitted => {
    submitState.reset()

    try {
      await submitLead(
        buildLeadPayload(form, submitted, {
          productName,
          today: dayjs().format('DD/MM/YYYY'),
        }),
      )
    } catch {
      // The mutation's error state shows the retry message.
      return
    }

    setIsSubmitted(true)

    if (onSubmitted) {
      redirectTimeoutId.current = window.setTimeout(
        onSubmitted,
        SUCCESS_REDIRECT_MS,
      )
    }
  })

  if (isSubmitted) {
    return (
      <Stack
        role="status"
        aria-live="polite"
        alignItems="center"
        spacing={2.5}
        sx={{ textAlign: 'center', py: 6 }}
      >
        <Box
          component="img"
          src="/icon_successtick.svg"
          alt=""
          sx={{ width: '7.5rem', height: '7.5rem' }}
        />
        <Text variant="h2" component="h2" color={textColor}>
          Thank you
        </Text>
        <Text variant="p" component="p" color={textColor}>
          {form.successMessage}
        </Text>
      </Stack>
    )
  }

  return (
    <>
      <Box
        component="form"
        onSubmit={onSubmit}
        noValidate
        sx={{ textAlign: 'center', width: '100%' }}
      >
        {form.fields.map(field => {
          const error = errors[field.salesforceName]?.message
          return (
            <LeadFormField
              key={field.salesforceName}
              field={field}
              id={`${idPrefix}-${field.salesforceName}`}
              value={values[field.salesforceName] ?? ''}
              error={typeof error === 'string' ? error : undefined}
              surface={surface}
              onChange={value =>
                setValue(field.salesforceName, value, {
                  shouldDirty: true,
                  shouldTouch: true,
                  shouldValidate: true,
                })
              }
            />
          )
        })}
        <FormSubmitWithRecaptcha
          submitLabel={form.submitLabel}
          buttonProps={{
            disabled: submitState.isLoading,
            sx: submitButtonSx[surface],
          }}
        />
      </Box>
      <iframe
        title="Salesforce lead submission"
        {...SALESFORCE_IFRAME_PROPS}
        style={{ display: 'none' }}
      />
      {submitState.isError && (
        <Text
          variant="p"
          component="p"
          color={surface === 'dark' ? 'white' : 'red'}
          sx={{ textAlign: 'center', py: 2 }}
        >
          Please try again, or contact us if the issue persists.
        </Text>
      )}
    </>
  )
}
